import React, { useCallback, useEffect, useRef, useState } from 'react';
import { tresorerieIa as api } from '../services/api';
import { mdVersHtml, dateHeure, usd, STATUT } from './MarcheAnalyseIa';
import './MarcheAnalyseIa.css';
import './Stock.css';   // st-seg

// ─── Analyse IA de la trésorerie — panneau latéral du rapport Trésorerie ─────
// Auto Antilles (2026-09-30). Même mécanique que l'analyse du marché, avec :
//   • le CHOIX des sociétés au lancement (bascule, une ou plusieurs), par
//     défaut celles cochées dans le panneau de filtres du rapport ;
//   • la PSEUDONYMISATION rendue visible : l'aperçu montre le texte exact qui
//     partirait, le résultat du contrôle de fuite, et la correspondance des
//     pseudonymes — gardée sur le serveur, jamais envoyée ;
//   • le rapport lu avec les noms réels, ou tel qu'Anthropic l'a reçu.

export default function TresorerieAnalyseIa({ selection, onFermer }) {
  const [liste, setListe] = useState(null);
  const [editable, setEditable] = useState(false);
  const [societes, setSocietes] = useState([]);
  const [choix, setChoix] = useState([]);
  const [courante, setCourante] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [erreur, setErreur] = useState('');
  const [attente, setAttente] = useState(false);
  const [brut, setBrut] = useState(false);   // texte tel que reçu (pseudonymisé)
  const minuteur = useRef(null);

  const chargerListe = useCallback(() => api.liste().then((r) => {
    setListe(r.data.analyses); setEditable(r.data.editable); setSocietes(r.data.societes || []);
    return r.data;
  }), []);
  const ouvrir = useCallback((id) => {
    clearTimeout(minuteur.current);
    api.lire(id).then((r) => {
      setCourante(r.data); setApercu(null);
      if (r.data.statut === 'en_cours') minuteur.current = setTimeout(() => ouvrir(id), 2000);
      else chargerListe();
    }).catch((e) => setErreur(e.response?.data?.error || 'Analyse introuvable'));
  }, [chargerListe]);

  useEffect(() => {
    chargerListe().then((d) => {
      const dispo = (d.societes || []).map((s) => s.code);
      const defaut = (selection || []).filter((c) => dispo.includes(c));
      setChoix(defaut.length ? defaut : dispo);
      if (d.analyses && d.analyses.length) ouvrir(d.analyses[0].id);
    }).catch((e) => setErreur(e.response?.data?.error || 'Erreur de chargement'));
    return () => clearTimeout(minuteur.current);
  }, [chargerListe, ouvrir]);   // eslint-disable-line react-hooks/exhaustive-deps

  const basculer = (code) => setChoix((l) => (l.includes(code) ? l.filter((c) => c !== code) : [...l, code]));
  const generer = async () => {
    setErreur(''); setAttente(true);
    try { const r = await api.lancer(choix); await chargerListe(); ouvrir(r.data.id); }
    catch (e) { setErreur(e.response?.data?.error || 'Génération impossible'); }
    finally { setAttente(false); }
  };
  const voirApercu = async () => {
    setErreur(''); setAttente(true);
    try { const r = await api.apercu(choix); setApercu(r.data); setCourante(null); }
    catch (e) { setErreur(e.response?.data?.error || 'Aperçu impossible'); }
    finally { setAttente(false); }
  };
  const exporter = () => {
    const b = new Blob([courante.contenu || ''], { type: 'text/markdown;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(b);
    a.download = `analyse-tresorerie-${String(courante.cree_le).slice(0, 10)}.md`; a.click(); URL.revokeObjectURL(a.href);
  };
  const correspondance = apercu?.correspondance
    ? [...Object.entries(apercu.correspondance.societes || {}), ...Object.entries(apercu.correspondance.poles || {}), ...Object.entries(apercu.correspondance.fournisseurs || {})]
    : [];

  return (
    <div className="ia-voile" onClick={onFermer}>
      <aside className="ia-panneau" onClick={(e) => e.stopPropagation()} aria-label="Analyse IA de la trésorerie">
        <div className="ia-tete">
          <div>
            <h2>Analyse IA de la trésorerie</h2>
            <div className="ia-sous">Auto Antilles · six derniers mois · données pseudonymisées avant envoi (sociétés, pôles, fournisseurs), aucun client, salarié ni libellé bancaire</div>
          </div>
          <button type="button" className="ia-fermer" onClick={onFermer} aria-label="Fermer" title="Fermer">✕</button>
        </div>

        {editable && (
          <div className="ia-actions">
            <span className="ia-note">Sociétés :</span>
            <span className="st-seg">{societes.map((s) => (
              <button key={s.code} type="button" className={choix.includes(s.code) ? 'on' : ''} onClick={() => basculer(s.code)}>{s.nom}</button>
            ))}</span>
          </div>
        )}
        <div className="ia-actions">
          {editable && <button type="button" className="ia-btn ia-btn-p" disabled={attente || !choix.length || courante?.statut === 'en_cours'} onClick={generer}>Générer une analyse</button>}
          {editable && <button type="button" className="ia-btn" disabled={attente || !choix.length} onClick={voirApercu}>Aperçu du dossier</button>}
          {(courante?.statut === 'termine' || courante?.statut === 'tronque') && <button type="button" className="ia-btn" onClick={exporter}>⬇ Markdown</button>}
          {!editable && <span className="ia-note">La génération est réservée aux administrateurs ; l'historique est consultable.</span>}
        </div>
        {erreur && <div className="ia-erreur">⚠️ {erreur}</div>}

        <div className="ia-corps">
          <div className="ia-histo">
            <div className="ia-histo-t">Historique</div>
            {liste === null ? <div className="ia-note">Chargement…</div> : !liste.length ? <div className="ia-note">Aucune analyse pour l'instant.</div> : liste.map((a) => (
              <button type="button" key={a.id} className={`ia-histo-l${courante?.id === a.id ? ' on' : ''}`} onClick={() => ouvrir(a.id)}>
                <b>{dateHeure(a.cree_le)}</b>
                <span>{(a.noms || []).length === societes.length && societes.length > 1 ? 'toutes les sociétés' : (a.noms || []).join(', ')}</span>
                {a.cout_usd != null && <span>{usd(a.cout_usd)}</span>}
                <span className={`ia-statut ia-${a.statut}`}>{STATUT[a.statut] || a.statut}</span>
              </button>
            ))}
          </div>

          <div className="ia-contenu">
            {apercu ? (
              <>
                <div className="ia-meta">Aperçu — rien n'a été envoyé · {apercu.modele} · prompt {apercu.version_prompt} · {Math.round(apercu.caracteres / 1000)} k caractères (≈ {Math.round(apercu.caracteres / 3200)} k tokens)</div>
                <div className={apercu.bloque ? 'ia-erreur' : 'ia-cours'}>
                  {apercu.bloque ? '⚠️ Envoi bloqué — ' : 'Contrôle de pseudonymisation : '}{apercu.controle}
                </div>
                <details open><summary>Correspondance des pseudonymes — gardée sur le serveur, jamais envoyée</summary>
                  <table className="ia-table"><tbody>{correspondance.map(([p, n]) => <tr key={p}><td>{p}</td><td>{n}</td></tr>)}</tbody></table>
                </details>
                <details><summary>Consigne système (envoyée)</summary><pre>{apercu.system}</pre></details>
                <details><summary>Message et dossier chiffré (envoyés)</summary><pre>{apercu.user}</pre></details>
              </>
            ) : !courante ? (
              <div className="ia-note">{editable ? 'Choisissez les sociétés, puis générez une analyse — ou ouvrez-en une dans l\'historique.' : 'Choisissez une analyse dans l\'historique.'}</div>
            ) : (
              <>
                <div className="ia-meta">
                  {dateHeure(courante.cree_le)} · {courante.auteur} · {(courante.noms || []).join(', ')} · {courante.periode} · {courante.modele} · prompt {courante.version_prompt}
                  {courante.statut !== 'en_cours' && courante.tokens_entree ? ` · ${Math.round(courante.tokens_entree / 1000)} k tokens lus, ${Math.round((courante.tokens_sortie || 0) / 1000)} k écrits · ${Math.round((courante.duree_ms || 0) / 1000)} s` : ''}
                  {courante.cout_usd != null ? <> · <b>{usd(courante.cout_usd)}</b></> : null}
                </div>
                {courante.contenu && (
                  <div className="ia-actions">
                    <span className="st-seg">
                      <button type="button" className={!brut ? 'on' : ''} onClick={() => setBrut(false)}>Noms réels</button>
                      <button type="button" className={brut ? 'on' : ''} onClick={() => setBrut(true)}>Tel que reçu (pseudonymisé)</button>
                    </span>
                  </div>
                )}
                {(courante.statut === 'echec' || courante.statut === 'tronque') && <div className="ia-erreur">⚠️ {courante.erreur}</div>}
                {courante.statut === 'en_cours' && <div className="ia-cours">Analyse en cours de rédaction… {courante.contenu ? '' : 'Le modèle étudie le dossier (une à deux minutes).'}</div>}
                <div className="ia-rapport" dangerouslySetInnerHTML={{ __html: mdVersHtml(brut ? courante.contenu_pseudonymise : courante.contenu) }} />
              </>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
